import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Car } from 'lucide-react';

const Register = () => {
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(roleParam || 'driver');
  const [carType, setCarType] = useState('Black Suburban');
  const [licensePlate, setLicensePlate] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (roleParam && ['driver', 'rider'].includes(roleParam)) {
      setRole(roleParam);
    }
  }, [roleParam]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { 
        name, 
        email, 
        password, 
        role,
        car_type: role === 'driver' ? carType : '',
        license_plate: role === 'driver' ? licensePlate : '',
        profile_photo_url: role === 'driver' ? profilePhoto : ''
      };
      const response = await axios.post(`${API}/auth/register`, payload);
      if (role === 'driver') {
        toast.success('Account created! Your account is pending activation by Onyx Reserve — you will be able to log in once activated.');
        navigate('/login');
      } else {
        login(response.data.access_token, response.data.user);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="register-card">
        <CardHeader className="space-y-3 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Car className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl sm:text-3xl font-bold">Create Account</CardTitle>
          <CardDescription className="text-base">Start tracking your ride income today</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Jose Rodriguez"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="form-input"
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="jose@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="form-input"
                data-testid="register-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Account Type</Label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md form-input"
                data-testid="register-role-select"
              >
                <option value="rider">Rider (Need a Ride)</option>
                <option value="driver">Driver (Provide Rides)</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Driver-specific fields */}
            {role === 'driver' && (
              <>
                <div className="border-t pt-4 space-y-4">
                  <h3 className="font-semibold text-gray-900">Driver Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="carType">Car Type *</Label>
                    <Input
                      id="carType"
                      type="text"
                      placeholder="e.g., Black SUV, Sedan, Luxury Car"
                      value={carType}
                      onChange={(e) => setCarType(e.target.value)}
                      required
                      className="form-input"
                      data-testid="register-car-type-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="licensePlate">License Plate Number *</Label>
                    <Input
                      id="licensePlate"
                      type="text"
                      placeholder="e.g., ABC1234"
                      value={licensePlate}
                      onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                      required
                      className="form-input"
                      data-testid="register-license-plate-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="profilePhoto">Profile Photo URL</Label>
                    <Input
                      id="profilePhoto"
                      type="url"
                      placeholder="https://example.com/your-photo.jpg"
                      value={profilePhoto}
                      onChange={(e) => setProfilePhoto(e.target.value)}
                      className="form-input"
                      data-testid="register-photo-url-input"
                    />
                    <p className="text-xs text-gray-500">
                      Upload your photo to a service like Imgur and paste the link here
                    </p>
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">Already have an account? </span>
            <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
